import { IconCheck, IconInfo, Tooltip } from '@leland-dev/leland-ui-library';
import React, {
  type FC,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react';

interface Props {
  label: ReactElement | string;
  isChecked: boolean;
  onToggle: ((newValue: boolean) => void) | null;
  containerClassName?: string;
  checkboxClassName?: string;
  className?: string;
  labelClassName?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltip?: ReactNode;
}

const CheckboxInput: FC<Props> = ({
  label,
  isChecked,
  onToggle,
  disabled,
  disabledTooltip,
  containerClassName,
  className,
  checkboxClassName,
  labelClassName,
  tooltip,
}) => {
  const labelKey = useId();

  const content = (
    <div className={`flex flex-row items-center ${containerClassName ?? ''}`}>
      <div
        className={`flex flex-row items-center ${
          onToggle != null && !disabled
            ? 'cursor-pointer'
            : 'cursor-not-allowed opacity-50'
        } ${className ?? ''}`}
        role="checkbox"
        aria-checked={isChecked ? 'true' : 'false'}
        aria-labelledby={labelKey}
        tabIndex={0}
        onClick={() => !disabled && onToggle != null && onToggle(!isChecked)}
        onKeyPress={(e) => {
          if (e.code === 'Space' && onToggle != null) onToggle(!isChecked);
        }}
        data-tooltip-id={labelKey}
      >
        <CheckboxBox
          isChecked={isChecked}
          checkboxClassName={checkboxClassName}
        />
        <span
          className={`select-none leading-snug ${labelClassName ?? ''}`}
          id={labelKey}
        >
          {label}
        </span>
      </div>
      {tooltip ? (
        <Tooltip content={tooltip}>
          <IconInfo className="ml-2 size-4 text-leland-gray-extra-light" />
        </Tooltip>
      ) : null}
    </div>
  );

  return disabled && disabledTooltip ? (
    <Tooltip content={disabledTooltip} side="bottom">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

interface CheckboxBoxProps {
  checkboxClassName?: string;
  isChecked: boolean;
}

const CheckboxBox = ({ checkboxClassName, isChecked }: CheckboxBoxProps) => {
  return (
    <div
      className={`flex items-center justify-center p-0.5 ${
        isChecked
          ? 'bg-leland-primary'
          : 'border border-leland-gray-stroke bg-white'
      } size-4.5 shrink-0 rounded ${checkboxClassName || 'mr-3'}`}
    >
      {isChecked ? <IconCheck color="white" className="size-3" /> : null}
    </div>
  );
};

export default CheckboxInput;
